/**
 * The Drive evidence panel.
 *
 * The step people skip is the sharing one, so it is numbered and the sentence about what
 * happens if HR cannot open the link is stated *before* submission, not discovered after
 * ([`14-evidence-drive.md`](../../../docs/14-evidence-drive.md)).
 *
 * The manager's folder is a transfer mechanism, not the archive of record: a folder in someone's
 * My Drive disappears when their Google account is deprovisioned. HR copies the files into the
 * org Shared Drive and pastes that link, which is what verification requires (D-22) — so this
 * folder only has to survive until HR processes the claim, and saying so is kinder than letting
 * people believe they must keep it forever.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Alert, Button, Card, Checkbox, Field, Input } from '@/components/ui';
import { ErrorNote } from '@/components/bits';
import type { Claim } from '@/lib/types';

const HR_MAILBOX = 'hrleaders@leychoon.com';

/** `HR035 / 2026 / IT Department / 2026-09-10 IT quarterly dinner — CL-2026-0007` */
export function suggestedFolderName(
  claim: {
    budget_year: number;
    department: { name: string | null };
    actual_date: string;
    activity_type: string;
    ref_no: string | null;
    proposal: { ref_no: string | null };
  },
  title?: string,
): string {
  const reference = claim.ref_no ?? claim.proposal.ref_no ?? 'draft';
  const what = title ?? claim.activity_type.replace(/_/g, ' ');
  return `HR035 / ${claim.budget_year} / ${claim.department.name ?? 'Department'} / ${claim.actual_date} ${what} — ${reference}`;
}

export function EvidencePanel({
  claim,
  editable,
  title,
}: {
  claim: Claim;
  editable: boolean;
  title?: string;
}) {
  const cache = useQueryClient();
  const [url, setUrl] = useState(claim.evidence.drive_url ?? '');
  const [ack, setAck] = useState(claim.evidence.shared_ack);
  const [copied, setCopied] = useState(false);

  const folder = suggestedFolderName(claim, title);

  const save = useMutation({
    mutationFn: () => api.setClaimEvidence(claim.id, { drive_url: url.trim(), shared_ack: ack }),
    onSuccess: () => cache.invalidateQueries({ queryKey: ['claim', claim.id] }),
  });

  const changed =
    url.trim() !== (claim.evidence.drive_url ?? '') || ack !== claim.evidence.shared_ack;

  return (
    <Card title="Receipts and photos">
      <ol className="grid gap-3 text-sm">
        <li className="grid gap-1.5">
          <span>
            <strong>1.</strong> Create a Drive folder. Suggested name, so HR can find the event
            without the app:
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <code className="bg-muted rounded px-2 py-1 text-xs break-all">{folder}</code>
            <Button
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(folder).then(() => setCopied(true));
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </li>
        <li>
          <strong>2.</strong> Upload <strong>all receipts and invoices</strong>, and at least one{' '}
          <strong>group photo</strong>.
        </li>
        <li>
          <strong>3.</strong> Share the folder with{' '}
          <code className="bg-muted rounded px-1.5 py-0.5 text-xs">{HR_MAILBOX}</code> — Viewer is
          enough.
        </li>
        <li>
          <strong>4.</strong> Paste the link below and confirm you have shared it.
        </li>
      </ol>

      <div className="mt-4 grid gap-3">
        <Field label="Drive folder link" required>
          {(props) => (
            <Input
              {...props}
              value={url}
              disabled={!editable}
              placeholder="https://drive.google.com/drive/folders/…"
              onChange={(event) => setUrl(event.target.value)}
            />
          )}
        </Field>

        <Checkbox
          checked={ack}
          disabled={!editable}
          onChange={(event) => setAck(event.target.checked)}
          label={`I have shared this folder with ${HR_MAILBOX}`}
          hint="Both the link and this confirmation are required to submit — the database refuses a submit without them."
        />

        <Alert tone="neutral">
          HR will open this link to check your receipts and photos, and will copy them into the HR
          archive. <strong>If they cannot open it, your claim will be sent back.</strong>
        </Alert>

        {editable ? (
          <div className="flex justify-end">
            <Button
              variant="primary"
              busy={save.isPending}
              disabled={!changed || url.trim() === ''}
              onClick={() => save.mutate()}
            >
              Save the evidence link
            </Button>
          </div>
        ) : null}

        {save.error ? <ErrorNote error={save.error} /> : null}
      </div>
    </Card>
  );
}

/** HR's half: the archive link, without which verification cannot pass (D-22). */
export function ArchivePanel({ claim }: { claim: Claim }) {
  const cache = useQueryClient();
  const [url, setUrl] = useState(claim.evidence.archive_url ?? '');

  const save = useMutation({
    mutationFn: () => api.setClaimArchive(claim.id, url.trim()),
    onSuccess: () => cache.invalidateQueries({ queryKey: ['claim', claim.id] }),
  });

  return (
    <Card title="Archive it (D-22)">
      <div className="grid gap-3 text-sm">
        <p className="text-muted-foreground">
          Copy the files from the submitter&rsquo;s folder into the org Shared Drive, then paste
          that link. The submitter&rsquo;s folder lives in their own My Drive and disappears with
          their account — this link is the durable record, and verification cannot pass without it.
        </p>

        {claim.evidence.drive_url ? (
          <p>
            Their folder:{' '}
            <a
              href={claim.evidence.drive_url}
              target="_blank"
              rel="noreferrer"
              className="break-all underline underline-offset-4"
            >
              {claim.evidence.drive_url}
            </a>
          </p>
        ) : (
          <p className="text-destructive">No folder link was provided.</p>
        )}

        <Field label="Archive link">
          {(props) => (
            <Input
              {...props}
              value={url}
              placeholder="https://drive.google.com/drive/folders/…"
              onChange={(event) => setUrl(event.target.value)}
            />
          )}
        </Field>

        <div className="flex justify-end">
          <Button
            variant="primary"
            busy={save.isPending}
            disabled={url.trim() === '' || url.trim() === (claim.evidence.archive_url ?? '')}
            onClick={() => save.mutate()}
          >
            Save the archive link
          </Button>
        </div>

        {save.error ? <ErrorNote error={save.error} /> : null}
      </div>
    </Card>
  );
}
